import * as React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { BusinessReportOutcomeDetails, BusinessReportType } from "./businessReports.types";
import { BusinessReportsOutcomeListRow } from "./outcomeListRow";
import { useDeleteBusinessReportOutcomeMutation, useGetBusinessReportsOutcomeQuery } from "./businessReports.api";
import { SortDropdown, SortFieldConfig, SortOption } from "@/components/sortDropdown";
import { Pagination } from "@/views/drive/pagination";
import { CircleLoaderLarge } from "@/components/ui/circleLoaderLarge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "react-toastify";
import { ErrorAlert } from "@/components/ui/errorAlert";
import { SnapshotCheckbox, type SnapshotCheckboxChangeMeta } from "@/components/snapshots/SnapshotCheckbox";
import { getNextRangeSelection } from "@/helpers/rangeSelection";
import { getOutcomeWorkspaceSummary } from "./workspaceSummary";

const emptyList: BusinessReportOutcomeDetails[] = [];

interface Props {
  tableRef: React.RefObject<HTMLDivElement | null>;
  templateType?: BusinessReportType;
  title?: string;
  emptyLabel?: string;
  variant?: "default" | "workspace";
}

interface SkeletonRowProps {
  index: number;
}

const SkeletonRow: React.FC<SkeletonRowProps> = ({ index }) => (
  <TableRow
    className="animate-in fade-in hover:bg-gray-50/50"
    style={{
      animation: `fadeIn 0.6s ease-out ${index * 0.08}s both`,
    }}
  >
    <TableCell className="whitespace-nowrap py-3 pl-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-20 animate-pulse" />
        <Skeleton className="h-3 w-32 animate-pulse" />
      </div>
    </TableCell>
    <TableCell className="py-3">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-48 animate-pulse" />
        <Skeleton className="h-3 w-40 animate-pulse" />
        <Skeleton className="h-4 w-32 animate-pulse" />
      </div>
    </TableCell>
    <TableCell className="hidden py-3 lg:table-cell">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 animate-pulse rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-24 animate-pulse" />
          <Skeleton className="h-3 w-32 animate-pulse" />
        </div>
      </div>
    </TableCell>
    <TableCell className="whitespace-nowrap py-3 pr-6 text-right">
      <div className="flex items-center justify-end gap-2">
        <Skeleton className="h-8 w-32 animate-pulse" />
        <Skeleton className="h-8 w-8 animate-pulse" />
      </div>
    </TableCell>
  </TableRow>
);

const LoadingState: React.FC = () => (
  <>
    <div className="relative overflow-hidden rounded-xl border bg-white shadow-sm">
      {/* Loader Section */}
      <div className="flex items-center justify-center border-b bg-gradient-to-r from-gray-50/50 via-white to-gray-50/50 py-8">
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-16 w-16 text-brand-gold-500 animate-in fade-in"
            style={{ animation: "fadeIn 0.6s ease-out" }}
          >
            <CircleLoaderLarge />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">Loading Snapshots</p>
            <p className="mt-1 text-xs text-muted-foreground">Fetching your recent items...</p>
          </div>
        </div>
      </div>

      {/* Table with Skeleton Rows */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="rounded-t-xl">
              <TableHead className="w-[180px] bg-gray-50/80 py-3 pl-6">Created</TableHead>
              <TableHead className="min-w-[300px] bg-gray-50/80 py-3">Details</TableHead>
              <TableHead className="hidden min-w-[280px] bg-gray-50/80 py-3 lg:table-cell">Owner</TableHead>
              <TableHead className="w-[100px] bg-gray-50/80 py-3 pr-6 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 10 }).map((_, i) => (
              <SkeletonRow key={i} index={i} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
    <style>{`
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>
  </>
);

export const BusinessReportsOutcomeList: React.FC<Props> = ({
  tableRef,
  templateType,
  title = "Recent Snapshots",
  emptyLabel = "No snapshots found.",
  variant = "default",
}) => {
  const [page, setPage] = React.useState(1);
  const [selectedSnapshots, setSelectedSnapshots] = React.useState<string[]>([]);
  const lastSelectedSnapshotIdRef = React.useRef<string | null>(null);
  const [deleteBusinessReportOutcome] = useDeleteBusinessReportOutcomeMutation();
  const [sortOption, setSortOption] = React.useState<SortOption>({
    field: "created_at",
    direction: "desc",
  });

  const sortFields: SortFieldConfig[] = [
    { key: "created_at", label: "Created Date" },
    { key: "name", label: "Name" },
  ];

  const { data, refetch, error } = useGetBusinessReportsOutcomeQuery(
    {
      offset: page - 1,
      descending: sortOption.direction === "desc",
      order_by: sortOption.field,
      ...(templateType ? { template_type: templateType } : {}),
    },
    { refetchOnMountOrArgChange: true },
  );

  const handleSelectSnapshot = React.useCallback(
    (id: string, checked: boolean, meta: SnapshotCheckboxChangeMeta) => {
      setSelectedSnapshots(prev =>
        getNextRangeSelection({
          orderedIds: data?.results.map(snapshot => snapshot.id) || [],
          selectedIds: prev,
          anchorId: lastSelectedSnapshotIdRef.current,
          targetId: id,
          targetChecked: checked,
          isRangeSelection: meta.shiftKey,
        }),
      );
      lastSelectedSnapshotIdRef.current = id;
    },
    [data?.results],
  );

  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      lastSelectedSnapshotIdRef.current = null;
      setSelectedSnapshots(checked ? data?.results.map(snapshot => snapshot.id) || [] : []);
    },
    [data?.results],
  );

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedSnapshots.map(id => deleteBusinessReportOutcome({ id })));
      toast.success(`${selectedSnapshots.length} snapshot(s) deleted successfully`);
      setSelectedSnapshots([]);
      refetch();
    } catch (error) {
      toast.error("Failed to delete selected snapshots");
      console.error("Delete error:", error);
    }
  };

  if (error) {
    return (
      <div className="w-full">
        <h3 className="mb-5 text-2xl font-semibold">{title}</h3>
        <ErrorAlert
          title={`Unable to load ${title.toLowerCase()}`}
          error={error}
          className="flex items-start justify-center p-8"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full">
        <h3 className="mb-5 text-2xl font-semibold">{title}</h3>
        <LoadingState />
      </div>
    );
  }

  const { results: businessReportOutcome = emptyList, total = 0 } = data;
  const isWorkspaceVariant = variant === "workspace";
  const workspaceSummary = getOutcomeWorkspaceSummary(businessReportOutcome, total);

  return (
    <>
      {!isWorkspaceVariant && <h3 className="mb-5 text-2xl font-semibold">{title}</h3>}
      {isWorkspaceVariant && (
        <section
          aria-label={`${title} summary`}
          className="mb-5 grid overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/90 shadow-none md:grid-cols-4"
        >
          <WorkspaceSummaryItem
            label={templateType === "business_report" ? "Reports" : "Snapshots"}
            value={workspaceSummary.total}
            help="Matching current view"
          />
          <WorkspaceSummaryItem label="Successful" value={workspaceSummary.successful} help="Completed items" />
          <WorkspaceSummaryItem label="Processing" value={workspaceSummary.processing} help="Currently running" />
          <WorkspaceSummaryItem label="Failed" value={workspaceSummary.failed} help="Need attention" />
        </section>
      )}
      <div className="w-full">
        <div
          className={
            isWorkspaceVariant
              ? "flex min-w-0 flex-wrap items-center gap-4 rounded-t-lg border border-b-0 border-[#ddd5cb] bg-[#fffdfb] px-5 py-4"
              : "relative mb-4 flex items-center justify-between"
          }
        >
          {isWorkspaceVariant && <h2 className="min-w-0 flex-1 truncate text-lg font-bold text-[#07142d]">{title}</h2>}
          {selectedSnapshots.length > 0 ? (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive" className="mb-2">
                  <Trash2 className="mr-2 h-4 w-4" /> Delete {selectedSnapshots.length} Snapshot
                  {selectedSnapshots.length !== 1 ? "s" : ""}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Snapshots</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete {selectedSnapshots.length} snapshot
                    {selectedSnapshots.length !== 1 ? "s" : ""}? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="secondary">Cancel</Button>
                  </DialogClose>
                  <Button variant="destructive" onClick={handleDeleteSelected}>
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : isWorkspaceVariant ? null : (
            <div />
          )}
          <SortDropdown currentSort={sortOption} onSortChange={setSortOption} fields={sortFields} className="mb-0" />
        </div>
        <div
          className={
            isWorkspaceVariant
              ? "relative overflow-hidden rounded-b-lg border border-[#ddd5cb] bg-[#fffdfb] shadow-none"
              : "relative overflow-hidden rounded-xl border bg-white shadow-sm"
          }
        >
          <div ref={tableRef} className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="rounded-t-xl">
                  <TableHead className="w-[50px] bg-[#f7f2ec] py-3 pl-6">
                    <SnapshotCheckbox
                      checked={
                        selectedSnapshots.length === businessReportOutcome.length && businessReportOutcome.length > 0
                      }
                      onCheckedChange={handleSelectAll}
                      ariaLabel="Select all snapshots"
                    />
                  </TableHead>
                  <TableHead className="w-[180px] bg-[#f7f2ec] py-3">Created</TableHead>
                  <TableHead className="min-w-[300px] bg-[#f7f2ec] py-3">Details</TableHead>
                  <TableHead className="hidden min-w-[280px] bg-[#f7f2ec] py-3 lg:table-cell">Owner</TableHead>
                  <TableHead className="w-[100px] bg-[#f7f2ec] py-3 pr-6 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businessReportOutcome.length ? (
                  businessReportOutcome.map(item => (
                    <BusinessReportsOutcomeListRow
                      key={item.id}
                      businessReportOutcome={item}
                      isSelected={selectedSnapshots.includes(item.id)}
                      onSelectSnapshot={(checked, meta) => handleSelectSnapshot(item.id, checked, meta)}
                      onToggleSnapshot={() =>
                        handleSelectSnapshot(item.id, !selectedSnapshots.includes(item.id), { shiftKey: false })
                      }
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      {emptyLabel}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <Pagination currentPage={page} totalPages={Math.ceil(total / 10)} onPageChange={setPage} />
      </div>
    </>
  );
};

interface WorkspaceSummaryItemProps {
  help: string;
  label: string;
  value: number;
}

const WorkspaceSummaryItem: React.FC<WorkspaceSummaryItemProps> = ({ help, label, value }) => (
  <div className="min-w-0 border-b border-[#e7e0d9] px-6 py-5 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0">
    <p className="truncate text-sm font-semibold text-gray-700">{label}</p>
    <p className="mt-1 text-3xl font-bold leading-none text-[#111b35]">{value}</p>
    <p className="mt-1 truncate text-xs font-medium text-gray-500">{help}</p>
  </div>
);
